import React from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { api, refusalMessage } from '../../lib/api';
import type { ClientTask } from './types';

export default function TaskTransitionDialog({ task, action, onClose, onSaved }: {
  task: ClientTask; action: 'complete' | 'cancel'; onClose: () => void; onSaved: () => void;
}) {
  const [outcome, setOutcome] = React.useState('');
  const [note, setNote] = React.useState('');
  const [proof, setProof] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const busyRef = React.useRef(false);
  const [error, setError] = React.useState<string | null>(null);
  // A retry keeps the exact action identity and fields, including after response loss.
  const intent = React.useRef<Record<string, unknown> | null>(null);
  const requiresProof = action === 'complete' && (['sent', 'scheduled', 'resolved'].includes(outcome) || ['invoice_sent', 'payment_received', 'lease_signed'].includes(task.task_type));
  const valid = Boolean(task.version && note.trim() && (action === 'cancel' || outcome) && (!requiresProof || proof.trim()));
  const save = async () => {
    if (!valid || busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(null);
    if (!intent.current) intent.current = { expected_version: task.version, action,
      idempotency_key: crypto.randomUUID(), outcome_code: action === 'complete' ? outcome : null,
      note: note.trim(), proof_ref: proof.trim() || null };
    try {
      await api.post(`/task-workflow/${task.id}/transitions`, intent.current);
      onSaved(); onClose();
    } catch (e) {
      setError(refusalMessage(e) || (e instanceof Error ? e.message : 'Could not confirm task update. Retry this same action.'));
    } finally { busyRef.current = false; setBusy(false); }
  };
  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle>{action === 'complete' ? 'Complete task' : 'Cancel task'}: {task.title}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
      {!task.version && <Alert severity="error">Task version is unavailable. Refresh the record before updating it.</Alert>}
      {error && <Alert severity="error">{error} The same action will be retried; refresh the record to verify an uncertain result.</Alert>}
      {action === 'complete' && <TextField select label="Outcome" value={outcome} onChange={e => setOutcome(e.target.value)} disabled={busy || Boolean(intent.current)}>
        {['connected', 'sent', 'no_answer', 'voicemail', 'scheduled', 'declined', 'resolved', 'not_completed'].map(value => <MenuItem key={value} value={value}>{value.replace(/_/g, ' ')}</MenuItem>)}
      </TextField>}
      <TextField required label={action === 'cancel' ? 'Reason for cancelling' : 'What happened?'} multiline value={note} onChange={e => setNote(e.target.value)} disabled={busy || Boolean(intent.current)} inputProps={{ maxLength: 2000 }} />
      {action === 'complete' && <TextField label="Proof reference" required={requiresProof} helperText="A message, file, appointment or other record that supports the outcome." value={proof} onChange={e => setProof(e.target.value)} disabled={busy || Boolean(intent.current)} inputProps={{ maxLength: 500 }} />}
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose} disabled={busy}>Back</Button><Button onClick={save} disabled={!valid || busy} variant="contained">{busy ? 'Saving…' : error ? 'Retry same action' : action === 'complete' ? 'Record outcome' : 'Cancel task'}</Button></DialogActions>
  </Dialog>;
}
